import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { useThemedStyles } from '../hooks/useThemedStyles';

// Only import on native — avoids web bundle errors
let NativeDateTimePicker: any = null;
if (Platform.OS !== 'web') {
  NativeDateTimePicker = require('@react-native-community/datetimepicker').default;
}

interface TimePickerFieldProps {
  label: string;
  /** "HH:MM" 24-hour string, matching how availability.timeRange stores times. */
  value: string;
  onChange: (time: string) => void;
}

const toDate = (time: string): Date => {
  const [h, m] = time.split(':').map(Number);
  const d = new Date();
  d.setHours(h || 0, m || 0, 0, 0);
  return d;
};

const toTimeString = (d: Date): string =>
  `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

const formatDisplay = (time: string): string => {
  const d = toDate(time);
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
};

/**
 * A real time picker for "HH:MM" string fields (e.g. Calendar availability's
 * timeRange) — mirrors DatePickerField.tsx's platform-branching pattern
 * (native spinner/clock, web's native <input type="time"> browser picker)
 * rather than free manual text entry.
 */
export const TimePickerField: React.FC<TimePickerFieldProps> = ({ label, value, onChange }) => {
  const s = useThemedStyles(getS);
  const [show, setShow] = useState(false);

  if (Platform.OS === 'web') {
    return (
      <View style={s.field}>
        <Text style={s.label}>{label}</Text>
        <input
          type="time"
          value={value}
          onChange={(e: any) => e.target.value && onChange(e.target.value)}
          style={webInputStyle as any}
        />
      </View>
    );
  }

  return (
    <View style={s.field}>
      <Text style={s.label}>{label}</Text>
      <TouchableOpacity style={s.picker} onPress={() => setShow(true)} activeOpacity={0.7}>
        <Ionicons name="time-outline" size={16} color={colors.textLight} />
        <Text style={s.pickerText}>{formatDisplay(value)}</Text>
      </TouchableOpacity>

      {show && NativeDateTimePicker && (
        <NativeDateTimePicker
          value={toDate(value)}
          mode="time"
          display={Platform.OS === 'ios' ? 'spinner' : 'default'}
          onChange={(_: any, selected?: Date) => {
            setShow(Platform.OS === 'ios');
            if (selected) onChange(toTimeString(selected));
          }}
        />
      )}
    </View>
  );
};

const webInputStyle = {
  padding: spacing.sm,
  fontSize: typography.fontSize.base,
  border: `1px solid ${colors.border as string}`,
  borderRadius: 8,
  minHeight: 48,
  width: '100%',
  boxSizing: 'border-box',
  color: colors.textDark,
  fontFamily: typography.fontFamily.regular,
  backgroundColor: colors.background.surface,
};

const getS = () => StyleSheet.create({
  field: { gap: spacing.xs },
  label: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    fontWeight: '600',
    fontFamily: typography.fontFamily.semibold,
  },
  picker: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    backgroundColor: colors.background.surface,
    minHeight: 48,
  },
  pickerText: {
    flex: 1,
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    fontFamily: typography.fontFamily.regular,
  },
});
